import { AlertColor, Box, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useContext, useState } from "react";
import { bodyshellService } from "../../services/bodyshellService";
import { useTranslation } from "react-i18next";
import BoxContent from "../../components/boxContent";
import DataGridApi from "../../components/dataGridApi";
import QuickAlert from "../../components/form/quickAlert";
import { AuthContext } from "../../contexts/AuthContext";
import ModalBodyShell from "../../components/bodyshell/ModalBodyShell";
import { IBodyShellDetail } from "../../services/bodyshellService";

export default function Bodyshell() {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);

  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalView, setOpenModalView] = useState(false);
  const [bodyshellSelected, setBodyshellSelected] =
    useState<IBodyShellDetail>();

  const [visibleAlert, setVisibleAlert] = useState<boolean>(false);
  const [messageQuickAlert, setMessageQuickAlert] = useState("");
  const [severityAlert, setSeverityAlert] = useState<AlertColor>("success");
  const [titleMessage, setTitleMessage] = useState("");
  const [dataUpdated, setDataUpdated] = useState(0);

  const updateData = () => {
    setDataUpdated((previous) => previous + 1);
  };

  const watchDataChange = (successMessage?: string) => {
    updateData();

    setVisibleAlert(true);
    setOpenModalAdd(false);
    setOpenModalEdit(false);
    setTitleMessage(t("quickAlert.sucess"));
    setMessageQuickAlert(successMessage ?? t("bodyshell.addedSuccess"));
    setSeverityAlert("success");
  };

  const messageError = (errorMessage?: string) => {
    setVisibleAlert(true);
    setTitleMessage(t("quickAlert.error"));
    setMessageQuickAlert(errorMessage ?? t("quickAlert.error"));
    setSeverityAlert("error");
  };

  const handleClickModalAdd = () => {
    setOpenModalAdd(true);
  };

  const handleEditClick = async (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    try {
      const { data } = await bodyshellService.getById(row.id, tokenHeaders);
      setBodyshellSelected(data);
      setOpenModalEdit(true);
    } catch {
      messageError();
    }
  };

  const handleViewClick = async (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    try {
      const { data } = await bodyshellService.getById(row.id, tokenHeaders);
      setBodyshellSelected(data);
      setOpenModalView(true);
    } catch {
      messageError();
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("bodyshell.columns.name"),
      flex: 1,
      minWidth: 150,
      headerClassName: "header-style",
    },
    {
      field: "scale",
      headerName: t("bodyshell.columns.scale"),
      flex: 1,
      minWidth: 150,
      headerClassName: "header-style",
    },
    {
      field: "vehicleAssoc",
      headerName: t("bodyshell.columns.models"),
      flex: 1,
      minWidth: 150,
      headerClassName: "header-style",
    },
    {
      field: "actions",
      headerName: t("bodyshell.columns.actions"),
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title={t("bodyshell.actions.view")}>
            <IconButton
              size="small"
              onClick={(e) => handleViewClick(e, params.row)}
            >
              <VisibilityIcon sx={{ fontSize: 20, color: "var(--blue-400)" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("bodyshell.actions.edit")}>
            <IconButton
              size="small"
              onClick={(e) => handleEditClick(e, params.row)}
            >
              <EditIcon sx={{ fontSize: 20, color: "var(--blue-400)" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      headerClassName: "header-style",
    },
  ];

  return (
    <BoxContent
      title={t("menu.bodyshell")}
      button
      titleButton={t("bodyshell.addBodyshell")}
      handleClick={handleClickModalAdd}
      //noGlobal
    >
      <QuickAlert
        visibility={visibleAlert}
        description={messageQuickAlert}
        severity={severityAlert}
        title={titleMessage}
        hanhleClose={() => setVisibleAlert(false)}
      />

      <Box sx={{ marginBottom: 3 }}>
        <span style={{ fontSize: 16, fontWeight: 400 }}>
          {t("bodyshell.description")}
        </span>
      </Box>

      <Box sx={{ marginBottom: 2 }}>
        <span style={{ fontSize: 12 }}>{t("bodyshell.searchDescription")}</span>
      </Box>

      <DataGridApi
        onRowClick={() => {}}
        dataUpdated={dataUpdated}
        endPoint="bodyshell?"
        columnsData={columns}
        messageError={messageError}
        onEmptyMessageApi={t("bodyshell.emptyMessage")}
        labelParams1={t("bodyshell.searchPlaceholder")}
        globalSearchEnabled
        globalSearchParam="&searchText="
        cleanButtonText={t("bodyshell.cleanButtonText")}
        cleanButtonType="outlined"
      />

      {openModalAdd && (
        <ModalBodyShell
          modalChange={setOpenModalAdd}
          onError={messageError}
          onSuccess={watchDataChange}
        />
      )}

      {openModalEdit && (
        <ModalBodyShell
          modalChange={setOpenModalEdit}
          onError={messageError}
          onSuccess={watchDataChange}
          mode="edit"
          data={bodyshellSelected}
        />
      )}

      {openModalView && (
        <ModalBodyShell
          modalChange={setOpenModalView}
          onError={messageError}
          mode="view"
          data={bodyshellSelected}
        />
      )}
    </BoxContent>
  );
}
